// Reminders and due: a Today and Tomorrow switch (a red dot on Today when a reminder is overdue). Reminders on top
// (tick to clear, Overdue and Late badges, the assistant's in violet, + opens the Add reminder form), Due below
// (type, owner and track, progress with how it stands, Open). Each part scrolls on its own, with "N more below".
import { useRef } from 'react';
import { motion } from 'motion/react';
import type { Reminder, TodayItem } from '@dpw/shared';
import { Orb, DrawnCheck } from '../../ui/motion';
import { Progress } from '../../ui/bits';
import { IconPlus } from '../../ui/icons';
import { spring } from '../../lib/motion';
import { dayLabel, localTime } from '../../lib/format';
import { TYPE_WORD, progressColor, progressText, remDay, remindersFor, useMoreBelow, weekdayOf } from './util';
import type { DrawerState } from './Drawer';

export function RemindersDue({ day, setDay, reminders, due, today, onToggle, onAdd, onOpen }: {
  day: 'today' | 'tomorrow'; setDay: (d: 'today' | 'tomorrow') => void; reminders: Reminder[]; due: { today: TodayItem[]; tomorrow: TodayItem[] };
  today: string; onToggle: (r: Reminder) => void; onAdd: () => void; onOpen: (s: DrawerState) => void;
}) {
  const remRef = useRef<HTMLDivElement>(null);
  const dueRef = useRef<HTMLDivElement>(null);
  useMoreBelow(remRef);
  useMoreBelow(dueRef);
  const lists = { today: remindersFor(reminders, 'today', today), tomorrow: remindersFor(reminders, 'tomorrow', today) };
  const rem = lists[day];
  const dues = due[day];
  const left = (l: Reminder[]) => l.filter(r => r.state !== 'done').length;
  const overdue = lists.today.some(r => r.state === 'overdue');
  const date = day === 'today' ? today : dues[0]?.date ?? '';
  const sub = day === 'today' ? dayLabel(today) : date ? dayLabel(date) : 'Tomorrow';
  return (
    <section className="rmc" aria-label="Reminders and due">
      <div className="sech"><div><b>Reminders and due</b></div>
        <div className="dsw" role="tablist" aria-label="Day">
          {(['today', 'tomorrow'] as const).map(k => (
            <button key={k} type="button" role="tab" aria-selected={day === k} className={`dsb${day === k ? ' on' : ''}`} onClick={() => setDay(k)}>
              {day === k && <motion.span layoutId="today-day" className="dsbpill" transition={spring.snappy} />}
              {k === 'today' && overdue && <i className="odot" title="A reminder is overdue" aria-hidden="true" />}
              <span className="dsl">{k === 'today' ? 'Today' : 'Tomorrow'}</span>{k === 'today' && overdue && <span className="vh">(a reminder is overdue)</span>}<span className="dsc">{left(lists[k])} rem · {due[k].length} due</span>
            </button>
          ))}
        </div>
      </div>
      <div className="grh">Reminders<button type="button" className="gadd" onClick={onAdd} aria-label="Add a reminder" title="Add a reminder"><IconPlus /></button>
        <span className="d">{sub}</span><span className="n">{left(rem)} left</span></div>
      <div className="pscr rem" ref={remRef}>
        {rem.length ? rem.map(r => {
          const ai = r.setBy === 'assistant';
          const isDone = r.state === 'done';
          const at = r.state === 'overdue' ? weekdayOf(remDay(r)) : r.remindAt ? localTime(r.remindAt) : 'Any time';
          const meta = ai ? `Set by the assistant${r.activity ? ` · for: ${r.activity.title}` : ''}`
            : r.setByOther ? `Set by ${r.setByUser?.name.split(' ')[0] ?? 'a teammate'} for you`
            : r.activity ? `${r.activity.title}${r.state === 'overdue' ? ` · was due ${weekdayOf(remDay(r))}` : r.state === 'late' && r.remindAt ? ` · was due ${localTime(r.remindAt)}` : ''}` : 'Added by you';
          return (
            <div key={r.id} data-row className={`rr${ai ? ' ai' : ''}${r.state === 'overdue' ? ' od' : ''}${r.state === 'late' ? ' lt' : ''}${isDone ? ' done' : ''}`}>
              <button type="button" className={`chk${isDone ? ' on' : ''}`} aria-pressed={isDone} aria-label={isDone ? `Not done: ${r.text}` : `Done: ${r.text}`} onClick={() => onToggle(r)}>{isDone ? <DrawnCheck size={14} /> : null}</button>
              <button type="button" className="rt" onClick={() => onOpen({ kind: 'reminder', id: r.id })}>
                <span className="t">{r.state === 'overdue' && <span className="odb">Overdue</span>}{r.state === 'late' && <span className="odb lt">Late</span>}{r.text}</span>
                <span className="m">{ai && <Orb size={8} />}{meta}</span>
              </button>
              <span className="tm">{at}</span>
            </div>
          );
        }) : <div className="emp2">No reminders</div>}
      </div>
      <div className="grh">Due<span className="d">{sub}</span><span className="n">{dues.length}</span></div>
      <div className="pscr due" ref={dueRef}>
        {dues.length ? dues.map(x => (
          <div key={x.id} data-row className={`du${x.state.bad ? ' bad' : ''}`}>
            <div className="db">
              <div className="r1"><span className={`lbl ${x.type === 'task' ? 'kb' : 'prep'}`}>{TYPE_WORD[x.type]}</span><b>{x.isMilestone ? '◆ ' : ''}{x.title}</b></div>
              <div className="r2">{x.owner?.split(' ')[0] ?? 'No owner'} · {x.trackName}</div>
              <span className="pp"><Progress value={x.progress} color={progressColor(x)} /><em>{progressText(x)}</em></span>
            </div>
            <span className="dt2">{x.start ?? 'Any time'}</span>
            <button type="button" className="bt" onClick={() => onOpen({ kind: 'item', id: x.id })} aria-label={`Open ${x.title}`}>Open</button>
          </div>
        )) : <div className="emp2">Nothing due</div>}
      </div>
    </section>
  );
}
